import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { MenuItem } from 'primeng/api';


export interface ColumnItem {
  field: string;
  mappingkey: string;
  header: string;
  ui_columnname:string;
  frozen?: boolean;
  [key: string]: any;
  stickyLeft:any;
}

interface CachedColumnState {
  selectedColumns: ColumnItem[];
  hiddenColumns: ColumnItem[];
}


@Injectable({
  providedIn: 'root',
})


export class GridCommonService {
  public cols: ColumnItem[] = [];
  public selectedColumns: ColumnItem[] = [];
  public hiddenColumns: ColumnItem[] = [];
  public selectedFields: string[] = [];
private defaultColumns: ColumnItem[] = [];
  public isDragging = false;
  public isCtrlPressed = false;
  public menuItems:MenuItem[] = [];
  public frozenFields: string[] = [];
  public isFrozenColumns: boolean = false;
  actionField = ['rsmsStatus', 'modifiedFlag'];

  private cacheKey: string = '';

  constructor(@Inject(DOCUMENT) private document: Document) {}
 
  init(initialColumns: ColumnItem[], storageKey: string) {
    this.cols = [...initialColumns];
    this.cacheKey = storageKey;
    this.defaultColumns = initialColumns.map(col=>({
      ...col,
      field:col.field ?? col.mappingkey ?? col.ui_columnname,
      frozen: false
    }));
    this.isDragging = false;
    this.selectedFields = [];

    const savedState = this.loadStateFromCache();

    if (savedState) {
      this.selectedColumns = savedState.selectedColumns;
      this.hiddenColumns = savedState.hiddenColumns;
    } else {
      this.selectedColumns = this.defaultColumns.map(c => ({ ...c }));
      this.hiddenColumns = [];
    }
  }

  onHeaderMouseDown(event: MouseEvent, field: string) {
    if(field === 'action' || this.actionField.includes(field)){
       event.preventDefault();
      return;
    }
    if (event.button === 0 && !event.ctrlKey && !event.metaKey) return;

    this.isCtrlPressed = event.ctrlKey || event.metaKey;

    if(this.hasRsmsStatusColumn() && this.actionField.includes(field)){
      return;
    }
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      event.stopPropagation();
      this.isDragging = true;
      
      if (this.selectedFields.includes(field)) {
        this.selectedFields = this.selectedFields.filter(f => f !== field);
      } else {
        this.selectedFields.push(field);
      }
      this.selectedFields = [...this.selectedFields];
    }
  }

  stopDragging() {
    this.isDragging = false;
    this.selectedFields = [...this.selectedFields];
  }

  onHeaderMouseEnter(field: string) {
    if (this.isDragging && !this.selectedFields.includes(field)) {
      this.selectedFields.push(field);
      this.selectedFields = [...this.selectedFields];
    }
  }

  onHeaderContextMenu(event: MouseEvent, field: string, contextMenuComponent: any, tableInstance: any) {

    if(field === 'action' || this.actionField.includes(field)){
      event.stopPropagation();
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (!this.selectedFields.includes(field)) {
      this.selectedFields = [field];
    }

    this.buildContextMenu(tableInstance);
    contextMenuComponent.show(event);
  }

public getColumnField(col: ColumnItem): string {
    return col.field ?? col.mappingkey ?? col.ui_columnname;
}

 private buildContextMenu(tableInstance: any) {
  const activeSelection = [...this.selectedFields];
  const selectedColObjects = this.selectedColumns.filter(col => activeSelection.includes(this.getColumnField(col)));
  
  const unfrozenSelectedCols = selectedColObjects.filter(col => !col.frozen);
  const frozenSelectedCols = selectedColObjects.filter(col => col.frozen);

  const unfrozenFields = unfrozenSelectedCols.map(col => this.getColumnField(col));
  const frozenFields = frozenSelectedCols.map(col => this.getColumnField(col));

  this.menuItems = [
    {
      label: `Hide Selected (${activeSelection.length})`,
      icon: 'pi pi-eye-slash',
      command: () => this.hideColumns(activeSelection, tableInstance)
    },
    {
      label: 'Unhide Columns',
      icon: 'pi pi-eye',
      visible: this.hiddenColumns.length > 0,
      items: [
        {
          label: 'Unhide All',
          icon: 'pi pi-images',
          command: () => this.unhideAllColumns(tableInstance)
        },
        { separator: true },
        ...this.hiddenColumns.map(col => ({
          label: col.header ?? col.ui_columnname,
          command: () => this.unhideColumn(col, tableInstance)
        }))
      ]
    },
    {
      label: `Freeze Selected (${unfrozenSelectedCols.length})`,
      icon: 'pi pi-lock',
      visible: unfrozenSelectedCols.length > 0, 
      command: () => this.freezeColumns(unfrozenFields, true, tableInstance)
    },
    {
      label: `Unfreeze Selected (${frozenSelectedCols.length})`,
      icon: 'pi pi-lock-open',
      visible: frozenSelectedCols.length > 0,
      command: () => this.freezeColumns(frozenFields, false, tableInstance)
    },
    { separator: true },
    {
      label: 'Reset All',
      icon: 'pi pi-refresh',
      command: () => this.resetAll(tableInstance)
    }
  ];
}

freezeColumns(fields: string[], freezeState: boolean, tableInstance: any): void {
  fields.forEach(field => {
    if (this.actionField.includes(field)) {
      return;
    }
    const col = this.selectedColumns.find(
      (c: any) => this.getColumnField(c) === field
    );

    if (!col) {
      return;
    }

    col.frozen = freezeState;
    if (freezeState) {
      this.frozenFields = this.frozenFields.filter(f => f !== field);
      this.frozenFields.push(field);
    } else {
      this.frozenFields = this.frozenFields.filter(f => f !== field);
    }
  });

  if(this.frozenFields.length > 0){
    this.isFrozenColumns = true;
  }
  else{
    this.isFrozenColumns = false;
  }

  const actionColumn = this.selectedColumns.find(c => this.actionField.includes((this.getColumnField(c))));
  
  const isActionAtStart = this.selectedColumns.length > 0 && this.actionField.includes(this.getColumnField(this.selectedColumns[0]));;

  if (this.frozenFields.length === 0) {
    this.selectedColumns = this.cols
      .filter(originalCol => !this.hiddenColumns.some(hc => this.getColumnField(hc) === this.getColumnField(originalCol)))
      .map(originalCol => {
        return { ...originalCol, frozen: false };
      });
  } else {
    const filteredFrozenFields = this.frozenFields.filter((f:any) => f !== this.actionField.includes(f));

    const frozenColumns: ColumnItem[] = [];
    filteredFrozenFields.forEach(field => {
      const col = this.selectedColumns.find(
        (c: ColumnItem) =>this.getColumnField(c) === field
      );
      if (col) {
        frozenColumns.push(col);
      }
    });

    const nonFrozenColumns = this.selectedColumns.filter(
      (col: any) => !col.frozen && !this.actionField.includes(this.getColumnField(col))
    );

    let contentColumns = [
      ...frozenColumns,
      ...nonFrozenColumns
    ];

    if (actionColumn) {
      if (isActionAtStart) {
        this.selectedColumns = [actionColumn, ...contentColumns];
      } else {
        this.selectedColumns = [...contentColumns, actionColumn];
      }
    } else {
      this.selectedColumns = contentColumns;
    }
  }

  this.selectedColumns = this.selectedColumns.map(col => {
    if (this.actionField.includes(this.getColumnField(col))) {
      col.frozen = false;
    }
    return col;
  });

  this.selectedFields = [];
  this.saveStateToCache();
  this.clearTableScroll(tableInstance);
}

  hideColumns(fields: string[], tableInstance: any) {
    if (this.selectedColumns.length <= fields.length) return; 
    const filteredField = fields.filter(field => !this.actionField.includes(field));
    this.hiddenColumns.push(...this.selectedColumns.filter(col => filteredField.includes(this.getColumnField(col))));
    this.selectedColumns = this.selectedColumns.filter(col => !filteredField.includes(this.getColumnField(col)));
    this.selectedFields = [];
    this.freezeColumns([], false, tableInstance);

  }

  unhideColumn(targetCol: ColumnItem, tableInstance: any) {
    this.selectedColumns.push(targetCol);
    const targetKey = this.getColumnField(targetCol);
    this.hiddenColumns = this.hiddenColumns.filter(col =>this.getColumnField(col) !== targetKey);
    this.freezeColumns([], false, tableInstance);
  }

  unhideAllColumns(tableInstance: any) {
    this.selectedColumns.push(...this.hiddenColumns);
    this.hiddenColumns = [];
    this.freezeColumns([], false, tableInstance);
  }

  onColumnsReordered(event: any, tableInstance: any) {
    const reorderedCols = event.columns ? [...event.columns] : [];
    if (reorderedCols.length === 0) {
      return
    };

    const frozenItems = reorderedCols.filter(c => c.frozen);
    const unfrozenItems = reorderedCols.filter(c => !c.frozen);

    let reorderSelectedColumns = [
      ...frozenItems,
      ...unfrozenItems,
    ];

    const actionCol = reorderSelectedColumns.find(c => this.actionField.includes(c.field));
    if(actionCol){
      reorderSelectedColumns = [actionCol, ...reorderSelectedColumns.filter(c => !this.actionField.includes(c.field))];
    }
    this.selectedColumns = reorderSelectedColumns;

    const currentHidden = this.hiddenColumns || [];
    this.cols = [
    ...this.selectedColumns,
    ...currentHidden
  ];

    this.selectedFields = [];
    this.isDragging = false;
    this.saveStateToCache();
    this.clearTableScroll(tableInstance);
  }
  hasFrozenColumns(): boolean {
    return this.selectedColumns?.some(
        (col:any) => !this.actionField.includes(this.getColumnField(col)) && col.frozen === true
    ) ?? false;
}

  hasRsmsStatusColumn():boolean {
    return this.cols?.some((col:any) => this.actionField.includes(this.getColumnField(col))) ?? false;
  }

  resetAll(tableInstance: any) {
    this.selectedColumns = this.defaultColumns.map(c => ({ ...c, frozen: false }));
    this.hiddenColumns = [];
    this.cols = this.selectedColumns.map(col=>({
      ...col,
      frozen: false
    }))
    this.selectedFields = [];
    this.frozenFields = [];
    this.isFrozenColumns =false;
    this.clearCache();
    this.clearTableScroll(tableInstance);
  }
  private saveStateToCache(): void {
    if (!this.cacheKey) return;
    const stateToSave: CachedColumnState = {
      selectedColumns: this.selectedColumns,
      hiddenColumns: this.hiddenColumns
    };
    localStorage.setItem(this.cacheKey, JSON.stringify(stateToSave));
  }

  private loadStateFromCache(): CachedColumnState | null {
    if (!this.cacheKey) return null;
    const cachedData = localStorage.getItem(this.cacheKey);
    if (!cachedData) return null;

    try {
      return JSON.parse(cachedData) as CachedColumnState;
    } catch (e) {
      console.error('Failed', e);
      return null;
    }
  }

  private clearCache(): void {
    if (this.cacheKey) {
      localStorage.removeItem(this.cacheKey);
    }
  }

  private clearTableScroll(tableInstance: any) {
    setTimeout(() => {
      if (tableInstance && typeof tableInstance.clearScrollState === 'function') {
        tableInstance.clearScrollState();
      }
    });
  }
  
}
